/**
 * Short English stories for reading practice. The text stays in English on purpose;
 * each locale has a one-line gist so younger readers know what it's about.
 */
export interface Story {
  id: StoryId;
  icon: string;
  title: string;
  lines: string[];
  /** Words added to the review deck after reading. */
  words: string[];
  quiz: { q: string; options: string[]; answer: number }[];
}

export type StoryId = 'kite' | 'mochi' | 'market' | 'dragon' | 'grandma' | 'comic' | 'tower' | 'gorge';

export const STORIES: readonly Story[] = [
  {
    id: 'kite',
    icon: '🪁',
    title: 'The Kite and the Wind',
    lines: [
      'Bo had a red kite with a long, long tail.',
      'One windy day, he ran across the park.',
      'The kite flew up, up, up — higher than the trees!',
      'Then the wind pushed it into the big tree.',
      'Bo was sad, but his friend had an idea.',
      '“Let’s shake the branches together!” she said.',
      'Shake, shake, shake… and down came the kite!',
      'Bo laughed. “Friends make everything easier.”',
    ],
    words: ['breeze', 'pond'],
    quiz: [
      { q: 'What colour was Bo’s kite?', options: ['Blue', 'Red', 'Green'], answer: 1 },
      { q: 'Where did the kite get stuck?', options: ['In a big tree', 'On a roof', 'In the pond'], answer: 0 },
      { q: 'How did they get the kite back?', options: ['They called the police', 'They bought a new one', 'They shook the branches together'], answer: 2 },
    ],
  },
  {
    id: 'mochi',
    icon: '🐱',
    title: 'Where Is Mochi?',
    lines: [
      'Mochi is a small white kitten.',
      'She lives with Grandma Lee near the park.',
      'One morning, Mochi chased a butterfly and got lost.',
      'Grandma looked everywhere: under the bench, behind the swing.',
      'Then she heard a tiny “mew” from a bush.',
      'Mochi was sleeping in the leaves!',
      'Grandma hugged her and said, “Never run away again, little one.”',
    ],
    words: ['butterfly', 'neighbor'],
    quiz: [
      { q: 'What colour is Mochi?', options: ['Black', 'Orange', 'White'], answer: 2 },
      { q: 'Why did Mochi get lost?', options: ['She chased a butterfly', 'She was hungry', 'It was raining'], answer: 0 },
      { q: 'Where was Mochi?', options: ['On the roof', 'In a bush', 'In the school'], answer: 1 },
    ],
  },
  {
    id: 'market',
    icon: '🏮',
    title: 'A Night at the Market',
    lines: [
      'On Saturday night, the street fills with lights and smells.',
      'Red lanterns swing above the stalls.',
      'Lily wants bubble tea, but she is a little shy.',
      'She takes a deep breath and says, “Could I have a bubble tea, please?”',
      'The auntie smiles. “Of course! Less sugar?”',
      '“Yes, please. Thank you!” says Lily.',
      'Her tea is sweet and cold, and Lily feels proud.',
    ],
    words: ['order', 'menu', 'proud'],
    quiz: [
      { q: 'What hangs above the stalls?', options: ['Red lanterns', 'Flags', 'Kites'], answer: 0 },
      { q: 'How does Lily feel at first?', options: ['Angry', 'A little shy', 'Sleepy'], answer: 1 },
      { q: 'How does Lily order?', options: ['She points and runs', 'She asks politely in English', 'She asks her mom'], answer: 1 },
    ],
  },
  {
    id: 'dragon',
    icon: '🐉',
    title: 'The Dragon Boat Race',
    lines: [
      'Every year, long boats with dragon heads race on the river.',
      'A drummer sits at the front. Boom, boom, boom!',
      'Everyone paddles at the same time, like one big dragon.',
      'If one person is too fast or too slow, the boat goes zigzag.',
      'Families watch from the bank and eat sticky rice dumplings.',
      'The winning team does not have the strongest people.',
      'It has the best teamwork!',
    ],
    words: ['team', 'practice'],
    quiz: [
      { q: 'Who sits at the front of the boat?', options: ['A drummer', 'A teacher', 'A cat'], answer: 0 },
      { q: 'What do families eat?', options: ['Pizza', 'Ice cream', 'Sticky rice dumplings'], answer: 2 },
      { q: 'Why does the winning team win?', options: ['They are the tallest', 'They have the best teamwork', 'Their boat is red'], answer: 1 },
    ],
  },
  {
    id: 'grandma',
    icon: '👵',
    title: 'When Grandma Was Young',
    lines: [
      'When Grandma Lee was a little girl, she lived on a tea farm in the hills.',
      'Every morning, she picked tea leaves with her sisters.',
      'There was no TV, but there were stars — thousands of them!',
      'She walked one hour to school, and she was never late.',
      'Her favourite thing was the rice cooker her father bought.',
      '“Everyone in the village came to see it!” Grandma laughs.',
      'Now that rice cooker is in her kitchen. It still works perfectly.',
    ],
    words: ['family', 'grateful'],
    quiz: [
      { q: 'Where did Grandma live as a girl?', options: ['In a big city', 'On a tea farm', 'Near the sea'], answer: 1 },
      { q: 'How long did she walk to school?', options: ['One hour', 'Five minutes', 'She took a bus'], answer: 0 },
      { q: 'What is still in her kitchen?', options: ['A TV', 'A bicycle', 'The old rice cooker'], answer: 2 },
    ],
  },
  {
    id: 'comic',
    icon: '🚀',
    title: 'Captain Rocket and the Moon Turtle',
    lines: [
      'Captain Rocket is a brave little turtle.',
      'He lives in a fish tank, but he dreams of space.',
      'One night, he built a rocket from a bottle cap!',
      '“Three, two, one… blast off!” he shouted.',
      'Whoosh! He flew past the fish, past the plants…',
      '…and landed on the sofa. Bo found him there in the morning.',
      '“Maybe next time, the moon,” said Captain Rocket.',
    ],
    words: ['excited', 'calm'],
    quiz: [
      { q: 'What kind of animal is Captain Rocket?', options: ['A turtle', 'A fish', 'A bird'], answer: 0 },
      { q: 'What did he build the rocket from?', options: ['A shoe', 'A bottle cap', 'A pencil'], answer: 1 },
      { q: 'Where did he land?', options: ['On the moon', 'In the pond', 'On the sofa'], answer: 2 },
    ],
  },
  {
    id: 'tower',
    icon: '🏙️',
    title: 'The Tower with a Golden Ball',
    lines: [
      'Taipei 101 is one of the tallest buildings in the world.',
      'It looks like bamboo, growing taller section by section.',
      'Its elevator is super fast: it goes up eighty-nine floors in under a minute!',
      'Inside the tower hangs a giant golden ball.',
      'When typhoons or earthquakes shake the building, the ball swings and keeps it steady.',
      'On New Year’s Eve, fireworks shoot out of the tower.',
      'People all over Taiwan count down together: three, two, one — Happy New Year!',
    ],
    words: ['excited', 'schedule'],
    quiz: [
      { q: 'What does Taipei 101 look like?', options: ['Bamboo', 'A tree', 'A boat'], answer: 0 },
      { q: 'What keeps the tower steady?', options: ['A big rope', 'A giant golden ball', 'Strong windows'], answer: 1 },
      { q: 'When are the fireworks?', options: ['On birthdays', 'Every Monday', 'On New Year’s Eve'], answer: 2 },
    ],
  },
  {
    id: 'gorge',
    icon: '⛰️',
    title: 'The Marble Mountains',
    lines: [
      'In Hualien, a turquoise river runs between tall marble cliffs.',
      'This place is called Taroko Gorge.',
      'The river cut through the rock for millions of years.',
      'Hikers walk on narrow trails and through dark tunnels.',
      'They wear helmets, because small rocks can fall.',
      'Swallows fly in and out of holes in the cliffs.',
      'At the end of the day, the hikers swim in the cold, clear sea at Qixingtan.',
    ],
    words: ['breeze', 'sunset'],
    quiz: [
      { q: 'What colour is the river?', options: ['Brown', 'Turquoise', 'Red'], answer: 1 },
      { q: 'Why do hikers wear helmets?', options: ['Because small rocks can fall', 'For fun', 'It is cold'], answer: 0 },
      { q: 'Which birds fly in the cliffs?', options: ['Penguins', 'Parrots', 'Swallows'], answer: 2 },
    ],
  },
];

export function story(id: StoryId): Story {
  return STORIES.find((s) => s.id === id)!;
}
